import { useRef, useState } from 'react';
import { safeErrorMessage } from '@/shared/utils/errors';

export type Page<T> = { items: T[]; total: number; offset: number };

// Shared load/paginate/error state for the admin list pages (Users, Grant History,
// Feedback, per-user Video Jobs) — each previously hand-rolled the same
// items/total/offset/loading/loadingMore/error state plus a `load(offset, append)`
// closure with an identical then/catch/finally shape. `fetchPage` is called fresh
// each time (it closes over whatever filter state the caller currently has), so
// callers don't need useCallback — `load`'s identity is expected to change every
// render, which only matters for the mount-only effect that already ignores it.
export function usePaginatedList<T>(fetchPage: (offset: number) => Promise<Page<T>>) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Which request is the current one. Without it the list showed whichever
     response ARRIVED last rather than whichever was REQUESTED last: change an
     admin filter while the previous page is still in flight and the older,
     slower response overwrites the newer one, leaving the table showing rows
     for a filter that is no longer set. Nothing about that looks like an error
     — it is a correct render of stale data, so it reads as the filter having
     silently failed. */
  const currentRequest = useRef(0);

  /* And which request owns each spinner.
     Two counters rather than one, because the two questions are different:
     `currentRequest` decides whose DATA wins, this decides whose FLAG does.
     The flags are per-kind — a `loadMore` sets `loadingMore` while a filter
     change sets `loading` — so one shared counter forces a bad choice. Clearing
     unconditionally lets an older request of the SAME kind switch a spinner off
     while a newer one is still running (invisible on a disabled button, visible
     on a full-page one); clearing only when current strands a `loadMore` that a
     filter change overtook, with a spinner nothing is left to clear.
     Per-kind, neither happens. */
  const currentByKind = useRef({ replace: 0, append: 0 });

  const load = (nextOffset: number, append: boolean) => {
    const request = (currentRequest.current += 1);
    const kind = append ? 'append' : 'replace';
    currentByKind.current[kind] = request;
    const superseded = () => request !== currentRequest.current;

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    fetchPage(nextOffset)
      .then((page) => {
        if (superseded()) return;
        setItems((prev) => (append ? [...prev, ...page.items] : page.items));
        setTotal(page.total);
        setOffset(page.offset);
      })
      .catch((err: unknown) => {
        // A superseded request's failure is not this list's failure either: it
        // would show an error banner over rows that loaded perfectly well.
        if (superseded()) return;
        setError(safeErrorMessage(err, 'Unable to load this list.'));
      })
      .finally(() => {
        // Guarded per KIND, not globally: a superseded `loadMore` still turns
        // its own spinner off, and an older replace no longer turns off a
        // spinner a newer replace is still using.
        if (currentByKind.current[kind] !== request) return;
        if (append) setLoadingMore(false);
        else setLoading(false);
      });
  };

  const hasMore = offset + items.length < total;

  /**
   * Fetch the next page.
   *
   * A no-op while any load is already running, and that guard is the answer to
   * a real question rather than defensive habit. `loadMore` computes its offset
   * from the list it can currently see, so issuing one while a REPLACE is in
   * flight asks the server for a page of a list that is about to be thrown
   * away — and then the request-id guard has to choose which of the two
   * responses to drop, with no good answer available. Dropping the replace
   * loses the filter change the user just made; dropping the append leaves a
   * spinner that fetched nothing.
   *
   * Refusing to start the second request makes the question moot. It also stops
   * a double-click on "Load more" from firing two appends, where the second
   * supersedes the first and quietly costs a page.
   *
   * Enforced rather than documented: an assumption written in a comment is one
   * the next caller has to remember.
   */
  const loadMore = () => {
    if (loading || loadingMore) return;
    load(offset + items.length, true);
  };

  return { items, setItems, total, offset, loading, loadingMore, error, setError, hasMore, load, loadMore };
}
